import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './styles.css';

/**
 * Tracks the on-screen keyboard so bottom-docked furniture can get out of its way.
 * iOS never shrinks the layout viewport while the keyboard is up, so a fixed bottom
 * bar just steals height behind it.
 */
function trackKeyboard(): void {
  const viewport = window.visualViewport;
  if (!viewport) return;

  const update = () => {
    const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
    document.documentElement.style.setProperty('--kb-inset', `${Math.round(inset)}px`);
    document.documentElement.dataset.keyboard = inset > 120 ? 'open' : 'closed';
  };

  viewport.addEventListener('resize', update);
  viewport.addEventListener('scroll', update);
  update();
}

/**
 * Registers the service worker and makes sure a new build actually reaches an
 * installed device.
 *
 * An installed iOS PWA is the hardest case: it can sit for days without a cold start,
 * and Safari will happily serve `sw.js` itself from the HTTP cache, so a device can stay
 * pinned to an old bundle indefinitely. Three things prevent that:
 *
 * - `updateViaCache: 'none'` forces the worker script itself to be revalidated.
 * - an update check runs on every return to the foreground, which for a home-screen app
 *   is the only reliable moment it is running and online.
 * - a waiting worker is told to activate rather than waiting for every tab to close,
 *   which in a standalone PWA may be never.
 */
function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  if (!import.meta.env.PROD) return;

  let registration: ServiceWorkerRegistration | null = null;

  const promoteWaiting = (reg: ServiceWorkerRegistration) => {
    if (reg.waiting) reg.waiting.postMessage('SKIP_WAITING');
  };

  const checkForUpdate = () => {
    if (!registration) return;
    if (document.visibilityState !== 'visible') return;
    void registration
      .update()
      .then(() => promoteWaiting(registration!))
      .catch(() => {
        /* offline, or the check raced a navigation — the next one will do */
      });
  };

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { updateViaCache: 'none' })
      .then((reg) => {
        registration = reg;
        promoteWaiting(reg);
        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              promoteWaiting(reg);
            }
          });
        });
      })
      .catch(() => {
        /* an app that cannot install offline still works online */
      });
  });

  document.addEventListener('visibilitychange', checkForUpdate);
  window.addEventListener('pageshow', checkForUpdate);

  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });
}

trackKeyboard();
registerServiceWorker();

const container = document.getElementById('root');
if (!container) throw new Error('Root element missing');

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
